import {
  IonItem,
  IonLabel,
  IonItemGroup,
  IonItemDivider,
  IonNote,
} from '@ionic/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import confetti from 'canvas-confetti'

import {
  calculateNextVaccinationDate,
  Family,
  FamilyMember,
} from '../data/family'
import './FamilyList.css'
import { useVisibility } from './TrackVisibility'

interface FamilyListItemProps {
  family: Family
  isFiltered: boolean
}

const getPercentage = (members: FamilyMember[]) => {
  const vaccinated = members.filter((m) => typeof m.vaccines === 'number' && m.vaccines > 0).length
  const percentage = (vaccinated / members.length) * 100

  return `${percentage.toFixed(2)}%`
}

const FamilyListItem: React.FC<FamilyListItemProps> = ({
  family: { name, members },
  isFiltered = false,
}) => {
  const [expanded, setExpanded] = useState(true)
  if (members.length === 0) {
    return null
  }

  const sortedMembers = members.sort((a, b) =>
    a.vaccinatedAt < b.vaccinatedAt ? 1 : -1
  )
  return (
    <IonItemGroup>
      <IonItemDivider onClick={() => setExpanded(!expanded)}>
        <IonLabel className="family-label">
          <div>{name}</div>
          {!isFiltered && <div>{getPercentage(members)}</div>}
        </IonLabel>
      </IonItemDivider>
      <div className={`member-list${expanded ? ' member-list--expanded' : ''}`}>
        {sortedMembers.map((member, index) => (
          <FamilyItem
            key={member.name}
            member={member}
            isLast={index === members.length - 1}
          />
        ))}
      </div>
    </IonItemGroup>
  )
}

const FamilyItem = ({
  member,
  isLast,
}: {
  member: FamilyMember
  isLast: boolean
}) => {
  const itemRef = useRef(null)

  const throwConfetti = useCallback(() => {
    if (itemRef.current && member.vaccines === 2) {
      //@ts-ignore
      const BOUNDS = itemRef.current.getBoundingClientRect()
      confetti({
        particleCount: 12,

        disableForReducedMotion: true,
        angle: 60,
        origin: {
          x: 40 / window.innerWidth,
          y: (BOUNDS.y + BOUNDS.height / 2) / window.innerHeight,
        },
      })
    }
  }, [])

  useVisibility(itemRef, throwConfetti)

  return (
    <IonItem
      lines={isLast ? 'none' : 'inset'}
      ref={itemRef}
      detail={false}
      className={member.vaccines === 2 ? 'fully-vaccinated' : ''}
      onClick={throwConfetti}
    >
      <div slot="start" className="dot">
        {member.vaccines === 0 ? (member.description ? '🗓' : '') : member.vaccines === 1 ? '💉' : '🎉'}
      </div>
      <IonLabel className="ion-text-wrap">
        <h2>
          {member.name}
          {member.vaccinatedAt && (
            <span className="date">
              <IonNote>{member.vaccinatedAt}</IonNote>
            </span>
          )}
        </h2>
        {member.vaccines && member.vaccines >= 1 ? (
          <p>
            {member.vaccines === 1
              ? 'Primeira dose'
              : 'Completamente vacinado!!'}
          </p>
        ) : null}
        {!member.description && member.vaccines === 1 ? (
          <p style={{ color: '#b067f9' }}>
            <strong>{calculateNextVaccinationDate(member.vaccinatedAt)}</strong>
          </p>
        ) : (
          member.description && <p style={{ color: '#b067f9' }}>
            <strong>{member.description}</strong>
          </p>
        )}
      </IonLabel>
    </IonItem>
  )
}

export default FamilyListItem
